import React from 'react';

import { useAccount, useUpdateAccount } from 'hooks/api/useAccount';
import { useRegistrationParams } from 'hooks/useRegistrationParams';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

/**
 * Syncs Ring account attributes with registration params.
 * Only syncs if the user is a Ring partner and on a Ring plan.
 */
export function useSyncRingAccountAttributes() {
  const { data: account } = useAccount();
  const { mutate: updateAccount } = useUpdateAccount();
  const { isLoading: isPlanLoading, isRingPlan } = useCurrentPlan();
  const { getParams } = useRegistrationParams();

  React.useEffect(() => {
    const registrationParams = getParams();

    // If no ring partner params in session, nothing to sync
    if (registrationParams?.partner !== 'ring') {
      return;
    }

    // If account hasn't loaded yet, wait
    if (!account) {
      return;
    }

    // If plan hasn't loaded yet, wait
    if (isPlanLoading) {
      return;
    }

    // Only sync if user is on a Ring plan
    if (!isRingPlan) {
      return;
    }

    const accountAttrs = account.account_attributes;
    const sessionAttrs = {
      redirect_uri: registrationParams.redirect_uri,
      state: registrationParams.state,
      ring_link_state: accountAttrs?.ring_link_state || ('unlinked' as const),
    };

    // Check if account attributes differ from session params
    const needsUpdate =
      !accountAttrs ||
      accountAttrs.redirect_uri !== sessionAttrs.redirect_uri ||
      accountAttrs.state !== sessionAttrs.state ||
      !accountAttrs.ring_link_state;

    if (needsUpdate) {
      updateAccount({
        account_attributes: sessionAttrs,
      } as any);
    }
  }, [account, getParams, isPlanLoading, isRingPlan, updateAccount]);
}
